import type { ActivityLevel, Goal, Profile } from '../store/types';

/**
 * Energy and protein targets.
 *
 * These are estimates from population equations, not measurements of you. Use
 * them as a starting point, then let the scale and the mirror over 2–3 weeks
 * tell you whether to adjust. Nothing here is medical advice, and if you ever
 * feel genuinely unwell on a deficit, eat more and talk to a doctor.
 */

const ACTIVITY_MULTIPLIER: Record<ActivityLevel, number> = {
  sedentary: 1.2,
  light: 1.375,
  moderate: 1.55,
  active: 1.725,
};

export const ACTIVITY_LABEL: Record<ActivityLevel, string> = {
  sedentary: 'Desk job, little movement outside the gym',
  light: 'On your feet some of the day',
  moderate: 'Active job or lots of walking',
  active: 'Physical job or very high daily step count',
};

export interface NutritionTargets {
  bmr: number;
  tdee: number;
  calories: number;
  proteinG: number;
  fatG: number;
  carbsG: number;
  deficitOrSurplus: number;
  expectedWeeklyChangeKg: number;
  explanation: string;
}

/** Mifflin-St Jeor — the most accurate of the simple BMR equations. */
export function basalMetabolicRate(profile: Profile): number {
  const { bodyweightKg: kg, heightCm: cm, age, sex } = profile;
  const base = 10 * kg + 6.25 * cm - 5 * age;
  return Math.round(sex === 'male' ? base + 5 : base - 161);
}

export function nutritionTargets(profile: Profile): NutritionTargets {
  const bmr = basalMetabolicRate(profile);
  const tdee = Math.round(bmr * ACTIVITY_MULTIPLIER[profile.activityLevel]);

  const { calories, explanation } = calorieTarget(tdee, profile.goal);
  const delta = calories - tdee;

  // Protein stays high in a deficit — it is what stops you losing muscle
  // alongside the fat, and it keeps you full.
  const proteinPerKg = profile.goal === 'fat_loss' ? 2.2 : profile.goal === 'muscle' ? 1.8 : 2.0;
  const proteinG = Math.round(profile.bodyweightKg * proteinPerKg);

  const fatG = Math.round(profile.bodyweightKg * 0.8);
  const carbsG = Math.max(0, Math.round((calories - proteinG * 4 - fatG * 9) / 4));

  // ~7700 kcal per kg of body fat.
  const expectedWeeklyChangeKg = Math.round(((delta * 7) / 7700) * 100) / 100;

  return {
    bmr,
    tdee,
    calories,
    proteinG,
    fatG,
    carbsG,
    deficitOrSurplus: delta,
    expectedWeeklyChangeKg,
    explanation,
  };
}

function calorieTarget(tdee: number, goal: Goal): { calories: number; explanation: string } {
  switch (goal) {
    case 'fat_loss':
      return {
        calories: Math.round(tdee * 0.8),
        explanation:
          'A 20% deficit. Aggressive enough to see change in a few weeks, gentle enough that your lifts hold up. Going harder mostly costs you muscle and gym performance, not fat.',
      };
    case 'muscle':
      return {
        calories: Math.round(tdee * 1.1),
        explanation:
          'A 10% surplus. Enough to build, small enough that most of the gain is muscle rather than fat.',
      };
    case 'strength':
      return {
        calories: Math.round(tdee * 1.05),
        explanation: 'A slight surplus to support heavy work and recovery.',
      };
    case 'recomp':
    default:
      return {
        calories: tdee,
        explanation:
          'Maintenance. Recomposition is slow but it works at your training age — protein high, lifts progressing, weight roughly flat while the mirror changes.',
      };
  }
}

/**
 * The honest answer to "how do I lose the fat on my lower abs".
 *
 * Worth saying plainly, because almost every fitness app implies otherwise and
 * sells ab programs on it.
 */
export const SPOT_REDUCTION_NOTE = {
  title: 'About targeting lower belly fat',
  body: [
    'You cannot choose where fat comes off. Training abs harder builds the muscle underneath, which is worth doing, but it does not burn the fat sitting on top of it. That is not a willpower problem — it is just how fat mobilisation works.',
    'Lower abdominal and lower-back fat is typically the last to go for men, and the order is set by genetics and hormones, not by your exercise selection. The upper abs showing before the lower is the normal pattern, not a sign anything is wrong.',
    'What actually moves it: a consistent moderate calorie deficit held for long enough, protein high enough to keep the muscle you have, lifting hard enough to give your body a reason to keep it, and sleep. Cardio helps by widening the deficit, not by targeting anything.',
    'At roughly 0.5% of bodyweight per week you are looking at months, not weeks. That is the honest timeline, and chasing it faster mostly costs you the muscle you spent years building.',
  ],
};

export interface WeeklyCardioTarget {
  sessions: number;
  minutes: number;
  calories: number;
  note: string;
}

export function weeklyCardioTarget(profile: Profile): WeeklyCardioTarget {
  const sessions = Math.max(2, profile.cardioDays.length);
  const minutes = profile.goal === 'fat_loss' ? sessions * 40 : sessions * 25;
  // Rough planning figure at a typical moderate MET of ~6.5.
  const calories = Math.round(((6.5 * 3.5 * profile.bodyweightKg) / 200) * minutes);

  return {
    sessions,
    minutes,
    calories,
    note:
      profile.goal === 'fat_loss'
        ? 'Add cardio to widen the deficit, not to replace eating properly. If you find yourself needing more and more of it to keep losing, fix the food instead.'
        : 'Enough to keep your conditioning and recovery good without eating into your lifting.',
  };
}
